import {
    Button,
    Input,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    FormControl,
    FormLabel,
    VStack,
    HStack,
    Spinner,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { User, UserCreateRequest, UserUpdateRequest } from "@bluefalcon/users-management-client-fetch";

type AddEditUserModalProps = {
    modalState: boolean;
    user?: User;
    handleAddUser: (data: UserCreateRequest) => Promise<void>;
    handleEditUser: (userId: string, req: UserUpdateRequest) => Promise<void>;
    closeModal: () => void;
    isLoading?: boolean;
};

export const AddEditUserModal = ({
    modalState,
    user,
    handleAddUser,
    handleEditUser,
    closeModal,
    isLoading,
}: AddEditUserModalProps) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [nickname, setNickname] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");

    useEffect(() => {
        setFirstName(user?.firstName ?? "");
        setLastName(user?.lastName ?? "");
        setNickname(user?.nickname ?? "");
        setEmail(user?.email ?? "");
        setPhoneNumber(user?.phoneNumber ?? "");
    }, [user]);

    const handleSubmit = async () => {
        if (user) {
            await handleEditUser(user.id, { nickname, phoneNumber, email });
        } else {
            await handleAddUser({
                email,
                firstName,
                lastName,
                nickname,
                phoneNumber,
                password: "@reset_me_123",
            });
        }
        onCloseModal();
    };

    const onCloseModal = () => {
        if (!user) {
            setFirstName("");
            setLastName("");
            setNickname("");
            setEmail("");
            setPhoneNumber("");
        }
        closeModal();
    };

    const validateForm = () => {
        return !firstName.trim() || !lastName.trim() || !email.trim() || !email.includes("@");
    };

    return (
        <Modal isOpen={modalState} onClose={onCloseModal}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{user ? "Edit User" : "Add User"}</ModalHeader>
                <ModalCloseButton />
                {!isLoading ? <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <HStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel htmlFor="firstName">First Name</FormLabel>
                                <Input
                                    id="firstName"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder="First Name"
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel htmlFor="lastName">Last Name</FormLabel>
                                <Input
                                    id="lastName"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    placeholder="Last Name"
                                />
                            </FormControl>
                        </HStack>
                        <FormControl isRequired>
                            <FormLabel htmlFor="email">Email</FormLabel>
                            <Input
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email"
                            />
                        </FormControl>
                        <HStack spacing={4}>
                            <FormControl>
                                <FormLabel htmlFor="phoneNumber">Phone Number</FormLabel>
                                <Input
                                    id="phoneNumber"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    placeholder="Phone #"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel htmlFor="nickname">Nickname</FormLabel>
                                <Input
                                    id="nickname"
                                    value={nickname}
                                    onChange={(e) => setNickname(e.target.value)}
                                    placeholder="Nickname"
                                />
                            </FormControl>
                        </HStack>
                    </VStack>
                </ModalBody> : <Spinner />}
                <ModalFooter>
                    <HStack spacing={3}>
                        <Button isLoading={isLoading} variant="outline" onClick={onCloseModal}>
                            Close
                        </Button>
                        <Button
                            isLoading={isLoading}
                            colorScheme="teal"
                            onClick={handleSubmit}
                            isDisabled={validateForm()}
                        >
                            {user ? "Edit User" : "Add User"}
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
